<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { email, required } from '@vuelidate/validators';
import { useVuelidate } from '@vuelidate/core';
import { splitName } from '@chatwoot/utils';
import {
  brazilianPhoneDigits,
  formatBrazilianDate,
  formatBrazilianPhone,
  formatCep,
  formatCnpj,
  formatCpf,
  onlyDigits,
} from 'shared/helpers/talkviaInputMasks';
import { useAlert } from 'dashboard/composables';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contactData: {
    type: Object,
    default: null,
  },
  isDetailsView: {
    type: Boolean,
    default: false,
  },
  // Painel da conversa: só identificação, documento, dados pessoais e endereço.
  hideSocialLinks: {
    type: Boolean,
    default: false,
  },
  isNewContact: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update']);

const { t } = useI18n();

const FORM_CONFIG = {
  FIRST_NAME: {
    field: 'firstName',
    label: 'Nome',
    placeholder: 'Nome do contato',
  },
  LAST_NAME: {
    field: 'lastName',
    label: 'Sobrenome',
    placeholder: 'Sobrenome do contato',
  },
  PHONE_NUMBER: {
    field: 'phoneNumber',
    label: 'Telefone',
    placeholder: 'Informe o telefone',
  },
  EMAIL_ADDRESS: {
    field: 'email',
    label: 'E-mail',
    placeholder: 'email@exemplo.com.br',
    type: 'email',
    fullWidth: true,
  },
};

const EMAIL_INVALID_MESSAGE = 'Informe um e-mail válido.';

const TALKVIA_PF_FIELD_CONFIG = [
  {
    key: 'cpf',
    label: 'CPF',
    placeholder: '000.000.000-00',
  },
  {
    key: 'rg',
    label: 'RG',
    placeholder: '00.000.000-0',
  },
  {
    key: 'matricula',
    label: 'Matrícula',
    placeholder: 'Matrícula do cliente',
  },
  {
    key: 'data_nascimento',
    label: 'Data de nascimento',
    placeholder: 'dd/mm/aaaa',
  },
  {
    key: 'nome_mae',
    label: 'Nome da mãe',
    placeholder: 'Nome completo da mãe',
  },
];

const TALKVIA_PJ_FIELD_CONFIG = [
  {
    key: 'razao_social',
    label: 'Razão social',
    placeholder: 'Nome empresarial',
  },
  {
    key: 'nome_fantasia',
    label: 'Nome fantasia',
    placeholder: 'Nome fantasia',
  },
  {
    key: 'empresa_telefone',
    label: 'Telefone da empresa',
    placeholder: '(00) 00000-0000',
    type: 'tel',
  },
  {
    key: 'empresa_telefone_alternativo',
    label: 'Telefone alternativo da empresa',
    placeholder: '(00) 00000-0000',
    type: 'tel',
  },
  {
    key: 'empresa_email',
    label: 'E-mail da empresa',
    placeholder: 'contato@empresa.com.br',
    type: 'email',
  },
];

const IDENTIFICATION_TITLE = 'Identificação';
const SOCIAL_TITLE = 'Links sociais';
const TALKVIA_SECTION_TITLE = 'Dados Pessoais';
const ADDRESS_SECTION_TITLE = 'Endereço';
const ZIP_CODE_LOADING_TEXT = 'Consultando CEP...';
const ADDRESS_FIELD_CONFIG = [
  {
    key: 'zipCode',
    label: 'CEP',
    placeholder: '00000-000',
  },
  {
    key: 'addressLine',
    label: 'Endereço',
    placeholder: 'Rua, avenida ou logradouro',
  },
  {
    key: 'addressNumber',
    label: 'Número',
    placeholder: 'Número',
  },
  {
    key: 'addressComplement',
    label: 'Complemento',
    placeholder: 'Apartamento, bloco, sala',
  },
  {
    key: 'neighborhood',
    label: 'Bairro',
    placeholder: 'Bairro',
  },
  {
    key: 'state',
    label: 'UF',
    placeholder: 'UF',
  },
];

const SOCIAL_CONFIG = {
  LINKEDIN: 'i-ri-linkedin-box-fill',
  FACEBOOK: 'i-ri-facebook-circle-fill',
  INSTAGRAM: 'i-ri-instagram-line',
  TELEGRAM: 'i-ri-telegram-fill',
  TIKTOK: 'i-ri-tiktok-fill',
  TWITTER: 'i-ri-twitter-x-fill',
  GITHUB: 'i-ri-github-fill',
};

const getDefaultState = () => ({
  id: 0,
  name: '',
  email: '',
  companyId: '',
  firstName: '',
  lastName: '',
  phoneNumber: '',
  additionalAttributes: {
    description: '',
    companyName: '',
    countryCode: '',
    country: '',
    city: '',
    zipCode: '',
    addressLine: '',
    addressNumber: '',
    addressComplement: '',
    neighborhood: '',
    state: '',
    socialProfiles: {
      facebook: '',
      github: '',
      instagram: '',
      telegram: '',
      tiktok: '',
      linkedin: '',
      twitter: '',
    },
  },
  customAttributes: {},
});

const state = reactive(getDefaultState());
const isFetchingZipCode = ref(false);
const isFetchingCnpj = ref(false);

const isEmpresa = computed(() => state.customAttributes?.tipo_pessoa === 'pj');

const talkviaFieldConfig = computed(() =>
  isEmpresa.value ? TALKVIA_PJ_FIELD_CONFIG : TALKVIA_PF_FIELD_CONFIG
);

// Legacy e-mails stored before validation keep the contact editable; new or changed values must be valid.
const originalEmail = ref('');
const validationRules = {
  firstName: { required },
  email: {
    validEmail: value =>
      !value || value === originalEmail.value || email.$validator(value),
  },
};

const v$ = useVuelidate(validationRules, state);

const isFormInvalid = computed(() => v$.value.$invalid);
const emitContactUpdate = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  const { firstName, lastName, ...stateWithoutNames } = state;
  emit('update', stateWithoutNames);
};

const prepareStateBasedOnProps = () => {
  if (props.isNewContact) {
    return; // Added to prevent state update for new contact form
  }

  const {
    id,
    name = '',
    email: emailAddress,
    phoneNumber,
    companyId = '',
    additionalAttributes = {},
    customAttributes = {},
  } = props.contactData || {};
  const { firstName, lastName } = splitName(name || '');
  const {
    description = '',
    companyName = '',
    countryCode = '',
    country = '',
    city = '',
    zipCode = '',
    addressLine = '',
    addressNumber = '',
    addressComplement = '',
    neighborhood = '',
    state: addressState = '',
    socialTelegramUserName = '',
    socialProfiles = {},
  } = additionalAttributes || {};

  const telegramUsername =
    socialProfiles?.telegram || socialTelegramUserName || '';
  originalEmail.value = emailAddress || '';

  Object.assign(state, {
    id,
    name,
    companyId: companyId || '',
    firstName,
    lastName,
    email: emailAddress,
    phoneNumber,
    additionalAttributes: {
      description,
      companyName,
      countryCode,
      country,
      city,
      zipCode,
      addressLine,
      addressNumber,
      addressComplement,
      neighborhood,
      state: addressState,
      socialProfiles: {
        ...socialProfiles,
        telegram: telegramUsername,
      },
    },
    customAttributes: {
      ...customAttributes,
    },
  });
};

const editDetailsForm = Object.entries(FORM_CONFIG).map(([key, config]) => ({
  key,
  ...config,
}));

const socialProfilesForm = computed(() =>
  Object.entries(SOCIAL_CONFIG).map(([key, icon]) => ({
    key,
    placeholder: t(`CONTACTS_LAYOUT.CARD.SOCIAL_MEDIA.FORM.${key}.PLACEHOLDER`),
    icon,
  }))
);

const isValidationField = key => {
  const field = FORM_CONFIG[key]?.field;
  return field === 'firstName' || field === 'email';
};

const getValidationKey = key => {
  return FORM_CONFIG[key]?.field;
};

const emitUpdatedState = () => {
  const { firstName, lastName, ...stateWithoutNames } = state;
  emit('update', stateWithoutNames);
};

// Creates a computed property for two-way form field binding
const getFormBinding = key => {
  const field = FORM_CONFIG[key]?.field;
  if (!field) return null;

  return computed({
    get: () => {
      // Handle firstName/lastName fields
      if (field === 'firstName' || field === 'lastName') {
        return state[field]?.toString() || '';
      }

      // Handle nested vs non-nested fields
      const [base, nested] = field.split('.');
      // Example: 'email' → state.email
      // Example: 'additionalAttributes.city' → state.additionalAttributes.city
      return (nested ? state[base][nested] : state[base])?.toString() || '';
    },

    set: async value => {
      // Handle name fields specially to maintain the combined 'name' field
      if (field === 'firstName' || field === 'lastName') {
        state[field] = value;
        // Example: firstName="John", lastName="Doe" → name="John Doe"
        state.name = `${state.firstName} ${state.lastName}`.trim();
      } else {
        // Handle nested vs non-nested fields
        const [base, nested] = field.split('.');
        if (nested) {
          // Example: additionalAttributes.city = "New York"
          state[base][nested] = value;
        } else {
          // Example: email = "test@example.com"
          state[base] = value;
        }
      }

      await emitContactUpdate();
    },
  });
};

const formatCompanyPhone = value =>
  formatBrazilianPhone(String(value ?? '').replace(/^\+55/, ''));

const customAttributeFormatters = {
  empresa_telefone: formatCompanyPhone,
  empresa_telefone_alternativo: formatCompanyPhone,
  cpf: formatCpf,
  cnpj: formatCnpj,
  data_nascimento: formatBrazilianDate,
};

const normalizeCompanyPhone = value => {
  const digits = brazilianPhoneDigits(value);
  return digits ? `+55${digits}` : '';
};

const customAttributeNormalizers = {
  empresa_telefone: normalizeCompanyPhone,
  empresa_telefone_alternativo: normalizeCompanyPhone,
  cpf: value => onlyDigits(value, 11),
  cnpj: value => onlyDigits(value, 14),
  data_nascimento: formatBrazilianDate,
};

const getCustomAttributeBinding = key =>
  computed({
    get: () => {
      const value = state.customAttributes?.[key]?.toString() || '';
      return customAttributeFormatters[key]?.(value) ?? value;
    },
    set: async value => {
      state.customAttributes[key] =
        customAttributeNormalizers[key]?.(value) ?? value;
      const isFormValid = await v$.value.$validate();
      if (isFormValid) emitUpdatedState();
    },
  });

const getAdditionalAttributeBinding = key =>
  computed({
    get: () => {
      const value = state.additionalAttributes?.[key]?.toString() || '';
      return key === 'zipCode' ? formatCep(value) : value;
    },
    set: async value => {
      state.additionalAttributes[key] =
        key === 'zipCode' ? onlyDigits(value, 8) : value;
      const isFormValid = await v$.value.$validate();
      if (isFormValid) emitUpdatedState();
    },
  });

const getMessageType = key => {
  return isValidationField(key) && v$.value[getValidationKey(key)]?.$error
    ? 'error'
    : 'info';
};

const cleanZipCode = value => onlyDigits(value, 8);

const lookupZipCode = async () => {
  const zipCode = cleanZipCode(state.additionalAttributes.zipCode);
  if (zipCode.length !== 8) return;

  isFetchingZipCode.value = true;
  try {
    const response = await fetch(`https://viacep.com.br/ws/${zipCode}/json/`);
    const data = await response.json();
    if (!response.ok || data.erro) throw new Error('CEP não encontrado');

    Object.assign(state.additionalAttributes, {
      zipCode,
      addressLine: data.logradouro || state.additionalAttributes.addressLine,
      neighborhood: data.bairro || state.additionalAttributes.neighborhood,
      city: data.localidade || state.additionalAttributes.city,
      state: data.uf || state.additionalAttributes.state,
      addressComplement:
        data.complemento || state.additionalAttributes.addressComplement,
      countryCode: state.additionalAttributes.countryCode || 'BR',
      country: state.additionalAttributes.country || 'Brazil',
    });
    emitUpdatedState();
  } catch {
    // Keep typed address when ViaCEP cannot locate the ZIP code.
  } finally {
    isFetchingZipCode.value = false;
  }
};

const cleanCnpj = value => onlyDigits(value, 14);

const toggleTipoPessoa = () => {
  state.customAttributes.tipo_pessoa = isEmpresa.value ? 'pf' : 'pj';
  emitUpdatedState();
};

const lookupCnpj = async () => {
  const cnpj = cleanCnpj(state.customAttributes.cnpj);
  if (cnpj.length !== 14 || isFetchingCnpj.value) return;
  const contactId = state.id;

  isFetchingCnpj.value = true;
  try {
    const response = await fetch(
      `https://brasilapi.com.br/api/cnpj/v1/${cnpj}`
    );
    if (!response.ok) throw new Error('CNPJ não encontrado');
    const data = await response.json();
    if (
      contactId !== state.id ||
      !isEmpresa.value ||
      cnpj !== cleanCnpj(state.customAttributes.cnpj)
    )
      return;

    state.customAttributes.razao_social = data.razao_social || '';
    state.customAttributes.nome_fantasia = data.nome_fantasia || '';

    const companyContacts = {
      empresa_telefone: normalizeCompanyPhone(data.ddd_telefone_1),
      empresa_telefone_alternativo: normalizeCompanyPhone(data.ddd_telefone_2),
      empresa_email: data.email || '',
    };
    Object.entries(companyContacts).forEach(([key, value]) => {
      if (!state.customAttributes[key]) state.customAttributes[key] = value;
    });

    if (data.cep) {
      const cep = String(data.cep).replace(/\D/g, '');
      Object.assign(state.additionalAttributes, {
        zipCode: cep,
        addressLine: data.logradouro || state.additionalAttributes.addressLine,
        addressNumber: data.numero || state.additionalAttributes.addressNumber,
        addressComplement:
          data.complemento || state.additionalAttributes.addressComplement,
        neighborhood: data.bairro || state.additionalAttributes.neighborhood,
        city: data.municipio || state.additionalAttributes.city,
        state: data.uf || state.additionalAttributes.state,
        countryCode: 'BR',
        country: 'Brazil',
      });
    }

    if (data.razao_social && !state.firstName) {
      state.name = data.nome_fantasia || data.razao_social;
      const { firstName, lastName } = splitName(state.name);
      state.firstName = firstName;
      state.lastName = lastName;
    }

    emitUpdatedState();
    useAlert('Dados do CNPJ carregados com sucesso.');
  } catch {
    useAlert('CNPJ não encontrado ou inválido.');
  } finally {
    isFetchingCnpj.value = false;
  }
};

const resetValidation = () => {
  v$.value.$reset();
};

const resetForm = () => {
  Object.assign(state, getDefaultState());
};

watch(
  () => props.contactData?.id,
  id => {
    if (id) prepareStateBasedOnProps();
  },
  { immediate: true }
);

// Expose state to parent component for avatar upload
defineExpose({
  state,
  resetValidation,
  isFormInvalid,
  resetForm,
});
</script>

<template>
  <div class="flex w-full min-w-0 flex-col gap-4">
    <section
      class="flex w-full flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4 sm:p-5"
    >
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ IDENTIFICATION_TITLE }}
      </span>
      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <template v-for="item in editDetailsForm" :key="item.key">
          <label
            v-if="item.key === 'PHONE_NUMBER'"
            class="flex flex-col gap-1.5 text-sm text-n-slate-12 sm:col-span-2"
          >
            <span>{{ item.label }}</span>
            <PhoneNumberInput
              v-model="getFormBinding(item.key).value"
              :placeholder="item.placeholder"
              show-border
              class="[&>div:first-child]:!h-10 [&_input]:!h-10 [&_input]:!py-2.5"
            />
          </label>
          <Input
            v-else
            v-model="getFormBinding(item.key).value"
            :type="item.type || 'text'"
            :placeholder="item.placeholder"
            :label="item.label"
            :message-type="getMessageType(item.key)"
            :message="
              item.key === 'EMAIL_ADDRESS' &&
              getMessageType(item.key) === 'error'
                ? EMAIL_INVALID_MESSAGE
                : ''
            "
            class="w-full"
            :class="{ 'sm:col-span-2': item.fullWidth }"
            @input="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
            @blur="
              isValidationField(item.key) &&
                v$[getValidationKey(item.key)].$touch()
            "
          />
        </template>
      </div>
    </section>
    <section
      class="flex w-full flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4 sm:p-5"
    >
      <div class="flex items-center justify-between w-full py-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ TALKVIA_SECTION_TITLE }}
        </span>
        <button
          type="button"
          class="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition"
          :class="
            isEmpresa
              ? 'bg-n-brand text-white'
              : 'bg-n-alpha-2 text-n-slate-11 hover:bg-n-alpha-3'
          "
          @click="toggleTipoPessoa"
        >
          <span
            class="size-3.5"
            :class="isEmpresa ? 'i-lucide-building-2' : 'i-lucide-user'"
          />
          {{ isEmpresa ? 'Empresa (CNPJ)' : 'Pessoa Física (CPF)' }}
        </button>
      </div>

      <div v-if="isEmpresa" class="flex w-full items-end gap-2">
        <Input
          v-model="getCustomAttributeBinding('cnpj').value"
          label="CNPJ"
          inputmode="numeric"
          maxlength="18"
          placeholder="00.000.000/0000-00"
          class="flex-1"
          @blur="lookupCnpj"
        />
        <Button
          icon="i-lucide-search"
          size="sm"
          color="slate"
          variant="outline"
          :is-loading="isFetchingCnpj"
          class="mb-0.5 h-8 shrink-0"
          @click="lookupCnpj"
        />
      </div>

      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          v-for="item in talkviaFieldConfig"
          :key="item.key"
          v-model="getCustomAttributeBinding(item.key).value"
          :label="item.label"
          :placeholder="item.placeholder"
          :type="item.type || 'text'"
          :inputmode="
            ['cpf', 'data_nascimento'].includes(item.key)
              ? 'numeric'
              : undefined
          "
          :maxlength="
            item.key === 'cpf'
              ? 14
              : item.key === 'data_nascimento'
                ? 10
                : undefined
          "
          class="w-full"
        />
      </div>

      <!-- eslint-disable-next-line vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
      <span v-if="isFetchingCnpj" class="text-xs text-n-slate-10">
        Consultando CNPJ...
      </span>
    </section>
    <section
      class="flex w-full flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4 sm:p-5"
    >
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ ADDRESS_SECTION_TITLE }}
      </span>
      <div class="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          v-for="item in ADDRESS_FIELD_CONFIG"
          :key="item.key"
          v-model="getAdditionalAttributeBinding(item.key).value"
          :label="item.label"
          :placeholder="item.placeholder"
          :inputmode="item.key === 'zipCode' ? 'numeric' : undefined"
          :maxlength="item.key === 'zipCode' ? 9 : undefined"
          class="w-full"
          @blur="item.key === 'zipCode' && lookupZipCode()"
        />
        <Input
          v-model="getAdditionalAttributeBinding('city').value"
          label="Cidade"
          placeholder="Cidade"
          class="w-full"
        />
      </div>
      <span v-if="isFetchingZipCode" class="text-xs text-n-slate-10">
        {{ ZIP_CODE_LOADING_TEXT }}
      </span>
    </section>
    <section
      v-if="!isNewContact && !hideSocialLinks"
      class="flex w-full flex-col gap-4 rounded-xl border border-n-weak bg-n-solid-1 p-4 sm:p-5"
    >
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ SOCIAL_TITLE }}
      </span>
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div
          v-for="item in socialProfilesForm"
          :key="item.key"
          class="flex min-w-0 items-center h-10 gap-2 px-3 rounded-lg border border-n-weak focus-within:border-n-brand"
          :class="{
            'bg-n-alpha-2 dark:bg-n-solid-2': isDetailsView,
            'bg-n-alpha-2 dark:bg-n-solid-3': !isDetailsView,
          }"
        >
          <Icon
            :icon="item.icon"
            class="flex-shrink-0 text-n-slate-11 size-4"
          />
          <input
            v-model="
              state.additionalAttributes.socialProfiles[item.key.toLowerCase()]
            "
            class="w-full min-w-0 text-sm bg-transparent outline-none reset-base text-n-slate-12 dark:text-n-slate-12 placeholder:text-n-slate-10 dark:placeholder:text-n-slate-10"
            :placeholder="item.placeholder"
            :aria-label="item.key"
            @input="emitUpdatedState"
          />
        </div>
      </div>
    </section>
  </div>
</template>
